import {customElement} from 'lit/decorators.js';
import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import type {Model} from './architecture.js';
import {Msg, SKELETON_ROW_COUNT, main} from './architecture.js';
import type {AudienceExportActivity} from '#lit/types/audience_exports.js';
import {TABLE_STATES, ExportStateUI} from '#lit/types/audience_exports.js';
import styles from './style.scss';
import {DataTableComponent} from '#lit/components/abstract/data_table/index.js';
import {applyRelevancy, RelevancyWeights} from '#lit/utils/fuzzysearch.js';

import {fuzzyMatch, safeToGrep} from '#lit/utils/strings.js';
import {paginate} from '#lit/utils/arrays.js';
import '#lit/components/base/tooltip/index.js';
import '#lit/components/base/bar_loader/index.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import type {Constructor} from 'lit-element';
import {audiencesLocale} from '#lit/common/locale/audiences.js';
import {locale} from '#lit/common/locale/index.js';

export const AUDIENCE_EXPORT_ACTIVITIES_TABLE_ELEMENT_TAG =
  'audience-show-export-activities-table';

/**
 * Export Audience Activities table
 */

@customElement(AUDIENCE_EXPORT_ACTIVITIES_TABLE_ELEMENT_TAG)
export default class AudienceExportActivitiesTable extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  static get styles() {
    return [styles, DataTableComponent.styles];
  }

  haystack(exportActivity: AudienceExportActivity): string {
    return (
      applyRelevancy(
        RelevancyWeights.HIGHEST,
        exportActivity.destination_name
      ) +
      applyRelevancy(RelevancyWeights.HIGH, exportActivity.status.state) +
      applyRelevancy(RelevancyWeights.MEDIUM, exportActivity.export_id.value)
    );
  }

  get isFiltered() {
    return Object.values(this.model.filters).some(x => !!x) || this.model.query;
  }

  get pagedData() {
    if (this.model.state === TABLE_STATES.LOADING) {
      return new Array(SKELETON_ROW_COUNT).fill({
        export_id: {value: null, short_version: null},
      });
    }

    return paginate(
      this.isFiltered ? this.model.filteredData : this.model.tableData,
      this.model.page,
      this.model.pageLimit
    );
  }

  updateResults() {
    let workingResults = this.model.tableData;

    if (this.model.query) {
      workingResults = workingResults.filter(activity =>
        fuzzyMatch(
          safeToGrep(this.model.query),
          safeToGrep(this.haystack(activity))
        )
      );
    }

    if (this.model.filters.status) {
      workingResults = workingResults.filter(activity =>
        this.model.filters.status === ExportStateUI.NotEmpty
          ? activity.status.state !== ExportStateUI.ExportEmpty
          : activity.status.state === this.model.filters.status
      );
    }

    if (this.model.filters.destination_name) {
      workingResults = workingResults.filter(
        activity =>
          activity.destination_name === this.model.filters.destination_name
      );
    }

    this.handleUpdate(Msg.setFilteredData(workingResults));
  }

  static get locale() {
    return audiencesLocale[locale.current].exportActivities;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [AUDIENCE_EXPORT_ACTIVITIES_TABLE_ELEMENT_TAG]: AudienceExportActivitiesTable;
  }
}
